import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import {
  MatAutocomplete,
  MatAutocompleteModule,
} from "@angular/material/autocomplete";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { Router } from "@angular/router";

import { OrdersService } from "@core/services/orders.service";
import { OrderListItem } from "@shared/models/order.models";
import { catchError, map, of, Subject, switchMap, tap } from "rxjs";

import { BaseDestroyableComponent } from "@juristech/web-shared";

import { QUICK_SEARCH_LIMIT } from "./constants";

interface QuickSearchResult {
  generation: number;
  orders: OrderListItem[];
  errored: boolean;
}

@Component({
  selector: "admin-order-quick-search",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: "./order-quick-search.component.html",
  styleUrls: ["./order-quick-search.component.scss"],
})
export class OrderQuickSearchComponent extends BaseDestroyableComponent {
  private readonly orders = inject(OrdersService);
  private readonly router = inject(Router);

  /** When set to a positive number, the matching row is rendered disabled
   *  and tagged "Current order". Defaults to 0 (no row disabled) when the
   *  widget is mounted outside the order-detail context. */
  readonly currentOrderId = input<number>(0);

  readonly searchTerm = signal("");
  readonly results = signal<OrderListItem[]>([]);
  readonly loading = signal(false);
  readonly errored = signal(false);
  readonly hasSubmitted = signal(false);

  readonly canSubmit = computed(
    () => this.searchTerm().trim().length > 0 && !this.loading(),
  );

  readonly displayBlank = (): string => "";

  private generation = 0;
  private readonly submit$ = new Subject<string>();

  constructor() {
    super();
    this.submit$
      .pipe(
        tap(() => {
          this.generation += 1;
          this.hasSubmitted.set(true);
          this.loading.set(true);
          this.errored.set(false);
        }),
        switchMap((term) => {
          const generation = this.generation;
          return this.orders
            .getOrders({
              page: 1,
              limit: QUICK_SEARCH_LIMIT,
              sortBy: "CreatedDate",
              sortOrder: "DESC",
              search: term,
            })
            .pipe(
              map(
                (res): QuickSearchResult => ({
                  generation,
                  orders: res.orders,
                  errored: false,
                }),
              ),
              catchError((err) => {
                console.error("Quick-search request failed", err);
                return of<QuickSearchResult>({
                  generation,
                  orders: [],
                  errored: true,
                });
              }),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ generation, orders, errored }) => {
        if (generation !== this.generation) return;
        this.loading.set(false);
        this.errored.set(errored);
        this.results.set(orders);
      });
  }

  /** Mirrors `ngModelChange` on the input. Resets a stale dropdown so prior
   *  results do not linger while the admin edits the term. */
  onTermChange(value: string): void {
    this.searchTerm.set(value);
    if (this.hasSubmitted()) {
      this.hasSubmitted.set(false);
      this.results.set([]);
      this.errored.set(false);
    }
  }

  /** Fires the search request. Called from the suffix-button click and from
   *  the Enter-key handler when the autocomplete panel is closed. No-ops on
   *  an empty trimmed term. */
  submitSearch(): void {
    const term = this.searchTerm().trim();
    if (term.length === 0) return;
    this.submit$.next(term);
  }

  /** Enter selects the highlighted option when the panel is open; otherwise
   *  it submits a fresh search. The gate prevents Material from emitting
   *  `optionSelected` AND `submitSearch` firing on the same keystroke. */
  onEnter(event: Event, panel: MatAutocomplete): void {
    if (panel.isOpen) return;
    event.preventDefault();
    this.submitSearch();
  }

  /** Keyboard-arrow + Enter path — `mat-autocomplete (optionSelected)`. */
  onOptionSelected(row: OrderListItem): void {
    this.navigateTo(row);
  }

  /** Mouse-click path on a row anchor. Plain click navigates via Router;
   *  modifier-click lets the browser open the `href` in a new tab. Always
   *  stops propagation so `(optionSelected)` does not double-fire. */
  onSelect(row: OrderListItem, event: MouseEvent): void {
    if (event.ctrlKey || event.metaKey || event.shiftKey) {
      event.stopPropagation();
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.navigateTo(row);
  }

  /** Anchor `href` for native middle-click / ctrl-click new-tab behaviour. */
  orderHref(row: OrderListItem): string {
    return `/orders/${row.OrderId}`;
  }

  private navigateTo(row: OrderListItem): void {
    if (row.OrderId === this.currentOrderId()) return;
    this.searchTerm.set("");
    this.results.set([]);
    this.hasSubmitted.set(false);
    this.router.navigate(["/orders", row.OrderId]);
  }
}
